import { Box, Typography, useTheme, Link } from "@mui/material";
import { ResourceWrapper } from "../../../../eventer-pod/client/ResourceWrapper.ts";
import EventTitle from "./event-title.tsx";
import { formatDateTimeRange } from "../../../utils/datetime-range-util.ts";
import Semantic from "../../../../eventer/Semantic.ts";
import { ConfirmationNumber } from "@mui/icons-material";
import { Idea } from "../../../w2d2d-api-client";
import IdeaTypeBadge from "../ideas/idea-type-badge";
import ParticipationView from "../ideas/participation-view";

interface EventProps {
    readonly event: ResourceWrapper
    readonly image: ResourceWrapper | null | undefined
    readonly location: ResourceWrapper | null | undefined
    readonly idea?: Idea | null | undefined
    /** Display mode: 'summary' for feed/list, 'detail' for full page (default: 'summary') */
    readonly mode?: 'summary' | 'detail'
    readonly remotePod?: string | null
}

export default function Event(props: EventProps) {
    const theme = useTheme()
    const { event, image, location, idea, mode = 'summary', remotePod } = props

    const description = event.getFirstString("https://schema.org/description")
    const startDate = event.getFirstString("https://schema.org/startDate")
    const endDate = event.getFirstString("https://schema.org/endDate")
    const parsedStartDate = startDate ? new Date(startDate) : null
    const parsedEndDate = endDate ? new Date(endDate) : null

    let dateText = parsedStartDate ? formatDateTimeRange({
        startDate: parsedStartDate,
        endDate: parsedEndDate || undefined
    }) : undefined

    const eventStatus = event.getFirstString(Semantic.SchemaOrg.Predicates.eventStatus.uri)
    if (eventStatus !== Semantic.SchemaOrg.Predicates.eventStatus.eventScheduled) {
        dateText = `[nicht öffentlich] ${dateText}`
    }

    const locationName = location?.getFirstString("https://schema.org/name")
    const imageContentUrl = image?.getFirstString("https://schema.org/contentUrl")
    const title = event.getFirstString("https://schema.org/name") ?? "(ohne Titel)"
    const ticketUrl = event.getFirstString("https://schema.org/offers")

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Date and Location */}
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>

                <Typography
                    variant="caption"
                    sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        fontSize: '0.75rem'
                    }}
                >
                    {dateText}
                </Typography>
                {locationName && (
                    <>
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.palette.text.disabled,
                                fontSize: '0.75rem'
                            }}
                        >
                            •
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '0.875rem'
                            }}
                        >
                            {locationName}
                        </Typography>
                    </>
                )}
            </Box>


            {/* Title */}
            <EventTitle event={event} />

            {/* Type Badge and Participation */}
            <Box sx={{ mt: 0.5, mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <IdeaTypeBadge idea={idea ?? null} remotePod={remotePod} />
                {mode === 'summary' && (
                    <ParticipationView idea={idea ?? null} />
                )}
            </Box>

            {/* Image */}
            {
                imageContentUrl && (
                    <Box
                        component="img"
                        src={imageContentUrl}
                        alt={image?.getFirstString("https://schema.org/caption") || title}
                        sx={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: 400,
                            objectFit: 'cover',
                            borderRadius: 1,
                            mt: 0.5,
                            mb: 0.5,
                        }}
                    />
                )
            }

            {/* Description */}
            {
                description && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.secondary,
                            lineHeight: 1.6,
                            mt: 0.5,
                            mb: 0.5,
                        }}
                    >
                        {description}
                    </Typography>
                )
            }

            {/* Ticket URL */}
            {
                mode === 'detail' && ticketUrl && (
                    <Box sx={{ mt: 0.5, mb: 0.5 }}>
                        <Link
                            href={ticketUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                textDecoration: 'none',
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    textDecoration: 'underline',
                                }
                            }}
                        >
                            <ConfirmationNumber fontSize="small" />
                            <Typography variant="body2">
                                Tickets
                            </Typography>
                        </Link>
                    </Box>
                )
            }
        </Box >
    )
}
